import { getDatabase, Database } from 'database';
import { Guid } from 'types/guid';
import { Dictionary } from 'types/Dictionary';
import * as i18nService from './i18n-service';
import { NmvsProvider } from 'types/nmvs-provider';
import { Tenant } from 'types/tenants/tenant';

export const TENANT_ID = 'TenantId';
export const CUSTOM_SETTINGS = 'CustomSettings';
export const HAS_ENTERPRISE_LOGIN = 'HasEnterpriseLogin';
export const LANGUAGE = 'Language';
export const COUNTRY_CODE = 'CountryCode';
export const NMVS_PROVIDER = 'NmvsProvider';

export async function getTenant(): Promise<Tenant> {
  const db = await getDatabase();
  return {
    id: await getTenantId(),
    countryCode: await getCountryCode(db),
    language: await getLanguageCodeInternal(db),
    customSettings: await getCustomSettings(db),
    hasEnterpriseLogin: await getHasEnterpriseLogin(db),
    nmvsProvider: await getNmvsProvider(db)
  } as Tenant;
}

export async function saveTenant(tenant: Tenant): Promise<void> {
  const db = await getDatabase();

  await saveTenantId(db, tenant.id);
  await saveHasEnterpriseLogin(db, tenant.hasEnterpriseLogin);
  await saveCustomSettings(db, tenant.customSettings);
  await saveLanguageCode(db, tenant.language);
  await saveCountryCode(db, tenant.countryCode);
  await saveNmvsProvider(db, tenant.nmvsProvider);
}

// tslint:disable-next-line:no-any
export async function getTenantId(): Promise<any> {
  // get tenantId from db
  const db = await getDatabase();
  const tenantId = await db.read(db.config.indexes.Tenant, TENANT_ID) as Guid;
  if (!tenantId) {
    return null;
  }
  return tenantId;
}

async function saveTenantId(db: Database, tenantId: Guid): Promise<void> {
  const existingTenantId = await db.read(db.config.indexes.Tenant, TENANT_ID) as Guid;
  if (!existingTenantId) {
    await db.add(db.config.indexes.Tenant, tenantId, TENANT_ID);
  }
}

async function getCustomSettings(db: Database): Promise<Dictionary<string>> {
  return await db.read(db.config.indexes.Tenant, CUSTOM_SETTINGS) as Dictionary<string>;
}

async function saveCustomSettings(db: Database, settings: Dictionary<string>): Promise<void> {
  if (!await getCustomSettings(db)) {
    await db.add(db.config.indexes.Tenant, settings, CUSTOM_SETTINGS);
  } else {
    await db.put(db.config.indexes.Tenant, settings, CUSTOM_SETTINGS);
  }
}

async function getHasEnterpriseLogin(db: Database): Promise<boolean> {
  return await db.read(db.config.indexes.Tenant, HAS_ENTERPRISE_LOGIN) as boolean;
}

async function saveHasEnterpriseLogin(db: Database, hasAuthSettings: boolean): Promise<void> {
  const existingAuthSettings = await getHasEnterpriseLogin(db);
  if (existingAuthSettings === null || existingAuthSettings === undefined) {
    await db.add(db.config.indexes.Tenant, hasAuthSettings, HAS_ENTERPRISE_LOGIN);
  } else {
    await db.put(db.config.indexes.Tenant, hasAuthSettings, HAS_ENTERPRISE_LOGIN);
  }
}

export async function getLanguageCode(): Promise<string> {
  const db = await getDatabase();
  return getLanguageCodeInternal(db);
}

async function getLanguageCodeInternal(db: Database): Promise<string> {
  return await db.read(db.config.indexes.Tenant, LANGUAGE) as string;
}

async function saveLanguageCode(db: Database, languageCode: string): Promise<void> {
  if (!await getLanguageCodeInternal(db)) {
    await db.add(db.config.indexes.Tenant, languageCode, LANGUAGE);
  } else {
    await db.put(db.config.indexes.Tenant, languageCode, LANGUAGE);
  }
  await i18nService.translateToLanguage(languageCode);
}

async function getCountryCode(db: Database): Promise<string> {
  return await db.read(db.config.indexes.Tenant, COUNTRY_CODE) as string;
}

async function saveCountryCode(db: Database, countryCode: string): Promise<void> {
  if (!await getCountryCode(db)) {
    await db.add(db.config.indexes.Tenant, countryCode, COUNTRY_CODE);
  } else {
    await db.put(db.config.indexes.Tenant, countryCode, COUNTRY_CODE);
  }
}

async function getNmvsProvider(db: Database): Promise<number> {
  return await db.read(db.config.indexes.Tenant, NMVS_PROVIDER) as number;
}

async function saveNmvsProvider(db: Database, nmvsProvider: NmvsProvider): Promise<void> {
  if (!await getNmvsProvider(db)) {
    await db.add(db.config.indexes.Tenant, nmvsProvider, NMVS_PROVIDER);
  } else {
    await db.put(db.config.indexes.Tenant, nmvsProvider, NMVS_PROVIDER);
  }
}

export async function translateToTenantLanguage() {
  const db = await getDatabase();
  const languageCode = await db.read(db.config.indexes.Tenant, LANGUAGE) as string;
  await i18nService.translateToLanguage(languageCode);
}


// WEBPACK FOOTER //
// ./src/services/tenant-service.ts